// Shared math + formatters used by the schedule/punch family of pre-built
// reports. Keep this file dependency-light — pure functions only, no React,
// no axios. As more labor reports land we can grow this module; old reports
// can adopt it during focused cleanup passes.

// shift_start / shift_end are stored as hours-since-midnight floats
// (9.5 = 9:30 AM, 26.5 = 2:30 AM next day). Convert to a printable string.
export function formatShiftTime(value) {
  if (value == null) return "—";
  const hours24 = ((value % 24) + 24) % 24;
  let h = Math.floor(hours24);
  const m = Math.round((hours24 - h) * 60);
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  const mm = m.toString().padStart(2, "0");
  return `${h}:${mm}${period}`;
}

// "Last, First" — matches Compeat's sort convention.
export function displayName(stm) {
  if (!stm) return "—";
  const last = (stm.last_name || "").trim();
  const first = (stm.first_name || "").trim();
  if (last && first) return `${last}, ${first}`;
  return last || first || "—";
}

export function formatCurrency(n, opts = {}) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: opts.minimumFractionDigits ?? 2,
    maximumFractionDigits: opts.maximumFractionDigits ?? 2,
  }).format(Number(n));
}

export function formatHours(n, digits = 2) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(digits);
}

// Some endpoints (http_redux_response) wrap their payload in
// { redux: { payload: ... } }. Defensive extractor that handles both
// shapes.
export function unwrapPayload(res) {
  return res?.data?.redux?.payload ?? res?.data ?? null;
}

// `get_actual_used_schedule` synthesizes a "dummy" ActualUsedSchedule row
// for every published shift that didn't get a matching punch — the view
// writes result_start = result_end = "no show" (literal string, with a
// space) and zeroes the clock times so the frontend knows it represents
// absence, not work. Anything iterating actuals for hours / wages /
// variance must skip these rows; counting them inflates Act Shifts and
// can produce extreme negative-variance dollars.
export function isNoShowActualShift(actualRow) {
  return (
    actualRow?.result_start === "no show" &&
    actualRow?.result_end === "no show"
  );
}

// Shift hours from start/end qh values, minus any total_break. Returns
// null if either bound is missing.
export function shiftDurationHours(start, end, totalBreak = 0) {
  if (start == null || end == null) return null;
  const s = Number(start);
  const e = Number(end);
  if (Number.isNaN(s) || Number.isNaN(e)) return null;
  const tb = Number(totalBreak) || 0;
  return Math.max(0, e - s - tb);
}

// Difference between actual clock-in and scheduled shift_start, expressed
// in MINUTES.
//   - Negative number → employee was early (e.g. -10 = 10 minutes early)
//   - Positive number → employee was late
// Returns null if either side is missing.
export function clockInVarianceMinutes(scheduledStart, actualStart) {
  if (scheduledStart == null || actualStart == null) return null;
  const sched = Number(scheduledStart);
  const act = Number(actualStart);
  if (Number.isNaN(sched) || Number.isNaN(act)) return null;
  return Math.round((act - sched) * 60);
}

// Categorize a clock-in variance using a tolerance threshold (in minutes).
// Compeat's Roster report defaults to a 60-minute window matched by job;
// we use a tighter default here but allow overrides per report.
export function clockInStatus(varianceMinutes, toleranceMinutes = 5) {
  if (varianceMinutes == null) return "missing";
  if (varianceMinutes <= -toleranceMinutes) return "early";
  if (varianceMinutes >= toleranceMinutes) return "late";
  return "on-time";
}

// Dollar impact of a clock-in/out variance: hours × hourly_wage. For
// "Only Earlies" / "Only Tardies" rollups Compeat sums these separately.
// We return a signed number — negative = saved $ (came in late / left
// early), positive = cost $ (came in early / left late). Caller decides
// how to sum.
//
// `hourlyWage` is the payroll-grade effective wage (look it up via
// lookupEffectiveWage from the get_effective_wages snapshot). Pass null
// when the employee has no matching hourly wage record.
export function varianceDollars(varianceMinutes, hourlyWage) {
  if (varianceMinutes == null || hourlyWage == null) return null;
  const wage = Number(hourlyWage);
  if (!Number.isFinite(wage) || wage <= 0) return null;
  return (varianceMinutes / 60) * wage;
}

// Look up an effective hourly wage from one pay period's snapshot.
// Mirrors the Paylocity export's precedence: role-specific wage first, then
// general wage fallback. Returns null if neither exists or the snapshot
// hasn't loaded yet.
//
//   snapshot shape: { "<stm_id>": { general, by_role: { "<role_id>": "..." } } }
//   stmId, scheduledRoleId can be number or string — we coerce to string.
//
// Note: scheduledRoleId is the ScheduledRole id (Role.id), the same value
// the backend uses for timecard.role_id and PublishedSchedule.role_id —
// NOT the StoreTeamMemberRole.id.
export function lookupEffectiveWage(snapshot, stmId, scheduledRoleId) {
  if (!snapshot || stmId == null) return null;
  const entry = snapshot[String(stmId)];
  if (!entry) return null;
  if (scheduledRoleId != null) {
    const roleWage = entry.by_role?.[String(scheduledRoleId)];
    if (roleWage != null) return roleWage;
  }
  return entry.general ?? null;
}

// Pull the right pay-period snapshot for a given shift date out of the
// range-aware get_effective_wages response. The endpoint walks each date in
// the request range, resolves its pay period (Paylocity-export-style), and
// returns one snapshot per distinct pay period plus a date → pay-period-start
// index. This lets a weekly business week that straddles a semi-monthly pay
// period cost each day with the correct wages.
//
//   wageData shape: {
//     effective_wages_by_pay_period: { "<pp_start_iso>": { <stm_id>: {...} } },
//     pay_period_by_date:           { "<date_iso>": "<pp_start_iso>" },
//   }
export function snapshotForDate(wageData, dateStr) {
  if (!wageData || !dateStr) return null;
  const ppStart = wageData.pay_period_by_date?.[dateStr];
  if (!ppStart) return null;
  return wageData.effective_wages_by_pay_period?.[ppStart] ?? null;
}

// Combined per-date wage lookup. The frontend's typical call site reads
// (stmId, roleId, shiftDate) and wants the rate — this saves callers from
// chaining snapshotForDate → lookupEffectiveWage themselves.
export function lookupEffectiveWageOnDate(
  wageData,
  dateStr,
  stmId,
  scheduledRoleId
) {
  const snapshot = snapshotForDate(wageData, dateStr);
  return lookupEffectiveWage(snapshot, stmId, scheduledRoleId);
}
